import {
	AddonApi,
	AvailableSubscriptionContentsApi,
	BaseSubscriptionApi,
	SubscriptionApi,
	SubscriptionsApi,
	UpdateAddonApi,
	UpdateSubscriptionApi,
	UpdateSubscriptionsApi,
} from "../../../../../../../../../services/Api/Subscription/subscriptionTypes";
import {
	PriceConfiguratorAddonApi,
	PriceConfiguratorAddonTermsApi,
	PriceConfiguratorContentApi,
} from "../../../../../../../../../services/Api/types";
import { OrganizationState } from "../../../../../../../../../services/organizations/types";

export const parseSubscriptionsToRequest = (
	configs: AvailableSubscriptionContentsApi,
	subscriptions: UpdateSubscriptionsApi
) => {
	const newSubscriptions = subscriptions.subscriptions.map((subscription) => {
		const newSubscription = {
			...subscription,
			monthly_sub_item: { ...subscription.monthly_sub_item },
		} as UpdateSubscriptionApi;
		const config = configs.contents.find((x) => x.family_id === newSubscription.family_id);
		if (!config) return newSubscription;
		const plan = config.plans.find((x) => x.plan_id === newSubscription.plan_id);
		if (!plan) return newSubscription;
		newSubscription.addons = defaultAddons(config, newSubscription.plan_id, newSubscription.addons);
		if (!newSubscription.status) newSubscription.status = "ACTIVE";
		if (newSubscription.status === "NEW") newSubscription.status = "ACTIVE";
		if (
			newSubscription.monthly_sub_item &&
			(!newSubscription.monthly_sub_item.addons || newSubscription.monthly_sub_item.addons.length <= 0)
		)
			newSubscription.monthly_sub_item = undefined;
		if (newSubscription.monthly_sub_item && plan.license_level === "ARCHIVE") {
			newSubscription.monthly_sub_item = undefined;
		}
		if (newSubscription.monthly_sub_item) {
			const newSubAddons = defaultSubAddons(
				config,
				newSubscription.plan_id,
				newSubscription.monthly_sub_item.addons
			);
			if (!newSubAddons || newSubAddons.length === 0) {
				newSubscription.monthly_sub_item = undefined;
			} else {
				newSubscription.monthly_sub_item.addons = newSubAddons;
			}
		}
		return newSubscription;
	});
	return newSubscriptions;
};

export const defaultSubAddons = (config: PriceConfiguratorContentApi, planId?: string, addons?: UpdateAddonApi[]) => {
	const plan = planId ? config.plans.find((x) => x.plan_id === planId) : config.plans[0];
	if (!plan) {
		return [];
	}
	if (!addons) {
		return config.plans[0].addons
			.filter((addon) => {
				return (
					addon.monthly_sub_details &&
					(addon.monthly_sub_details.requirement === "MANDATORY" ||
						addon.monthly_sub_details.requirement === "RECOMMENDED")
				);
			})
			.map((addon) => {
				return defaultAddon(addon);
			});
	}
	const newAddons = addons.filter((addon) => {
		const addonFromConfig = plan?.addons.find((f) => f.monthly_sub_details && f.addon_id === addon.addon_id);
		if (!addonFromConfig) return false;
		return true;
	});
	return newAddons;
};

export const defaultAddons = (config: PriceConfiguratorContentApi, planId?: string, addons?: UpdateAddonApi[]) => {
	const plan = planId ? config.plans.find((x) => x.plan_id === planId) : config.plans[0];
	if (!plan) {
		return [];
	}
	if (!addons) {
		return plan.addons
			.filter((addon) => {
				return addon.details.requirement === "MANDATORY" || addon.details.requirement === "RECOMMENDED";
			})
			.map((addon) => {
				return defaultAddon(addon);
				// return {
				//     addon_id: addon.addon_id,
				//     quantity: addon.details.min_value ? addon.details.min_value : addon.details.quantity
				// } as UpdateAddonApi;
			});
	}
	const newAddons = addons.filter((addon, index) => {
		const addonFromConfig = plan?.addons.find((f) => f.addon_id === addon.addon_id);
		if (!addonFromConfig) return false;
		return true;
	});
	plan.addons
		.filter((addon) => {
			return addon.details.requirement === "MANDATORY";
		})
		.forEach((addon) => {
			if (!newAddons.some((x) => x.addon_id === addon.addon_id)) {
				newAddons.push(defaultAddon(addon));
			}
		});
	return newAddons;
};

export const defaultAddon = (config: PriceConfiguratorAddonApi) => {
	return {
		addon_id: config.addon_id,
		quantity:
			config.details.requirement === "MANDATORY" || config.details.requirement === "RECOMMENDED"
				? config.details.min_value
					? config.details.min_value
					: config.details.quantity
				: 1,
	} as UpdateAddonApi;
};

const parseSubscriptionToAddonUpdate = (addon: AddonApi) => {
	return {
		addon_id: addon.addon_id,
		quantity: addon.quantity,
	} as UpdateAddonApi;
};

export const parseSubscriptionToUpdate = (subscription?: BaseSubscriptionApi) => {
	if (!subscription) return undefined;
	return {
		// external_id: subscription.external_id,
		edit_mode: subscription.edit_mode,
		family_id: subscription.family_id,
		billing_period: subscription.billing_period,
		plan_id: subscription.plan_id,
		addons: subscription.addons.map((addon) => {
			return parseSubscriptionToAddonUpdate(addon);
		}),
		status: !subscription.status ? "ACTIVE" : subscription.status,
		cancellation_reason: subscription.cancellation_reason,
		reason_details: subscription.reason_details,
	} as UpdateSubscriptionApi;
};

export const findSameSubscription = (
	subs: SubscriptionApi[],
	familyId: string,
	editMode: string,
	externalId?: string
) => {
	if (externalId) return subs.find((f) => f.external_id === externalId) as SubscriptionApi;
	return subs.find((f) => f.family_id === familyId && f.edit_mode === editMode) as SubscriptionApi;
};

export const getLicenseLevel = (config: PriceConfiguratorContentApi, planId: string) => {
	const plan = config.plans.find((x) => x.plan_id === planId);
	if (!plan) return undefined;
	return plan.license_level;
};
export const isArchivePlan = (config: PriceConfiguratorContentApi, planId: string) => {
	return getLicenseLevel(config, planId) === "ARCHIVE";
};
export const isSubscriptionActive = (status: string) => {
	return status === "ACTIVE" || status === "IN_TRIAL";
};

export const checkIfDisplaySubscription = (subscription: SubscriptionApi) => {
	return (
		subscription.status !== "CANCELLED" &&
		subscription.status !== "NON_RENEWING" &&
		subscription.status !== "PAUSED"
	);
};

export const getTotalMonthlyAddonsPrice = (subscription: SubscriptionApi, organization: OrganizationState) => {
	const currency = organization?.currency || "";
	if (!subscription) return { amount: 0, currency };
	if (subscription.monthly_sub_item) {
		if (subscription.monthly_sub_item.addons?.length === 0) return { amount: 0, currency };
		return {
			amount:
				subscription.monthly_sub_item?.addons.reduce((acc, cur) => acc + Number(cur?.total_price?.amount), 0) ||
				0,
			currency: subscription.total_money.currency || currency,
		};
	}
	return { amount: 0, currency };
};

export const isWarningInEstimatedSubscriptionData = (estimate: SubscriptionsApi) => {
	return estimate.subscriptions.some((subscription) =>
		subscription.addons.find((addon) => addon.warning?.code === "invalid_subscription_for_usage")
	);
};

export const getParsedTermsNameToSnakeCase = (termName: string) => {
	return termName.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
};
